import { FAMILY_SCREEN_V2_EARLY_ACCESS_FEATURE_FLAG } from '../featureFlags';
import { v2Typography } from '../Families/v2Typography';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import {
  Container,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { useScreenTitle } from '../Shell/ShellScreenTitle';
import { PersonName } from '../Families/PersonName';
import { FamilyName } from '../Families/FamilyName';
import { EmojiPeople, Event } from '@mui/icons-material';
import { AppNavigate, useAppNavigate } from '../Hooks/useAppNavigate';
import { QueueItem, useQueueItems } from '../Model/QueueModel';
import { ReactElement } from 'react';
import { format } from 'date-fns';

interface InboxMessageProps {
  icon: ReactElement;
  onClick: () => void;
  primaryContent: ReactElement;
  secondaryContent?: ReactElement;
}

function getMessageProps(
  item: QueueItem,
  appNavigate: AppNavigate
): InboxMessageProps {
  switch (item.type) {
    case 'ChildOver18':
      return {
        icon: <EmojiPeople color="error" />,
        onClick: () => {
          const familyId = item.family.family?.id;
          const familyMemberId = item.child.id;

          if (familyId && familyMemberId) {
            appNavigate.family(familyId, { familyMemberId });
            return;
          }

          if (familyId) {
            appNavigate.family(familyId);
          }
        },
        primaryContent: (
          <>
            <Typography
              variant="body1"
              sx={{ display: 'inline', fontWeight: 'bold' }}
            >
              Child over 18:{' '}
            </Typography>
            <PersonName person={item.child} />
          </>
        ),
        secondaryContent: <FamilyName family={item.family} />,
      };

    case 'MissingPrimaryContact':
      return {
        icon: <EmojiPeople color="error" />,
        onClick: () => appNavigate.family(item.family.family!.id!),
        primaryContent: (
          <Typography
            variant="body1"
            sx={{ display: 'inline', fontWeight: 'bold' }}
          >
            Family missing a primary contact
          </Typography>
        ),
        secondaryContent: <FamilyName family={item.family} />,
      };

    case 'MissingLastName':
      return {
        icon: <EmojiPeople color="warning" />,
        onClick: () => {
          const familyId = item.family.family?.id;
          const familyMemberId = item.person.id;

          if (familyId && familyMemberId) {
            appNavigate.family(familyId, { familyMemberId });
          }
        },
        primaryContent: (
          <>
            <Typography
              variant="body1"
              sx={{ display: 'inline', fontWeight: 'bold' }}
            >
              Missing last name:{' '}
            </Typography>
            <PersonName person={item.person} />
          </>
        ),
        secondaryContent: <FamilyName family={item.family} />,
      };

    case 'ChildNotReturned':
      return {
        icon: <EmojiPeople sx={{ color: 'red' }} />,
        onClick: () => {
          const familyId = item.family.family?.id;
          const v1CaseId = item.v1CaseId;
          const arrangementId = item.arrangementId;

          if (familyId && v1CaseId && arrangementId) {
            appNavigate.family(familyId, { v1CaseId, arrangementId });
          }
        },
        primaryContent: (
          <Typography
            variant="body1"
            sx={{ fontWeight: 'bold', color: 'black' }}
          >
            Child not returned to parent: {item.child.firstName}{' '}
            {item.child.lastName}
          </Typography>
        ),
        secondaryContent: (
          <Typography variant="body2" sx={{ color: 'black' }}>
            <FamilyName family={item.family} />
          </Typography>
        ),
      };

    case 'ArrangementDueToStart':
      return {
        icon: <Event color="warning" />,
        onClick: () => {
          const familyId = item.family.family?.id;
          const v1CaseId = item.v1CaseId;
          const arrangementId = item.arrangementId;

          if (familyId && v1CaseId && arrangementId) {
            appNavigate.family(familyId, { v1CaseId, arrangementId });
          }
        },
        primaryContent: (
          <>
            <Typography
              variant="body1"
              sx={{ display: 'inline', fontWeight: 'bold' }}
            >
              Arrangement due to start:{' '}
            </Typography>
            <PersonName person={item.child} /> - {item.arrangementType}
          </>
        ),
        secondaryContent: (
          <Typography variant="body2" sx={{ color: 'black' }}>
            <FamilyName family={item.family} /> - planned to start{' '}
            {format(item.plannedStartUtc, 'M/d/yyyy')}
          </Typography>
        ),
      };

    case 'ClosedCaseInvalidArrangementStatus':
      return {
        icon: <Event color="error" />,
        onClick: () => {
          const familyId = item.family.family?.id;
          const v1CaseId = item.v1CaseId;
          const arrangementId = item.arrangementId;

          if (familyId && v1CaseId && arrangementId) {
            appNavigate.family(familyId, { v1CaseId, arrangementId });
          }
        },
        primaryContent: (
          <>
            <Typography
              variant="body1"
              sx={{ display: 'inline', fontWeight: 'bold' }}
            >
              Closed case has an ongoing arrangement:{' '}
            </Typography>
            <PersonName person={item.child} /> - {item.arrangementType}
          </>
        ),
        secondaryContent: (
          <Typography variant="body2" sx={{ color: 'black' }}>
            <FamilyName family={item.family} />
          </Typography>
        ),
      };
  }
}

function InboxMessage({
  icon,
  onClick,
  primaryContent,
  secondaryContent,
}: InboxMessageProps) {
  return (
    <ListItemButton
      disableGutters
      sx={{ paddingTop: 0, paddingBottom: 0 }}
      onClick={onClick}
    >
      <ListItemIcon sx={{ minWidth: 34 }}>{icon}</ListItemIcon>
      <ListItemText primary={primaryContent} secondary={secondaryContent} />
    </ListItemButton>
  );
}

function MessageList() {
  const appNavigate = useAppNavigate();
  const queueItems = useQueueItems();

  const messages = (queueItems ?? []).map((item) =>
    getMessageProps(item, appNavigate)
  );

  return (
    <List>
      {messages.map((message, i) => (
        <ListItem key={i} disableGutters>
          <InboxMessage {...message} />
        </ListItem>
      ))}
    </List>
  );
}

export function InboxScreen() {
  const isV2 =
    useFeatureFlagEnabled(FAMILY_SCREEN_V2_EARLY_ACCESS_FEATURE_FLAG) === true;
  useScreenTitle('Inbox');

  return (
    <Container disableGutters maxWidth={false} sx={{ display: 'flow-root' }}>
      {isV2 && (
        <Typography
          className="ph-unmask"
          {...v2Typography.pageTitle}
          component="h1"
          sx={{ mt: 2, mb: 2 }}
        >
          Inbox
        </Typography>
      )}
      <MessageList />
    </Container>
  );
}
